import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { formatDateTime } from "../../../../utils/dateTimeUtils";
import {
  Button,
  TextField,
  Checkbox,
  Typography,
  Box,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  IconButton,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContentText,
  DialogContent,
  Autocomplete,
  Switch,
} from "@mui/material";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardDoubleArrowDownOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowDownOutlined";
import KeyboardDoubleArrowUpOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowUpOutlined";

import {
  ArrowBack,
  History,
  FileCopy,
  Replay,
  Add,
  Delete,
  Print,
  Approval,
  Save,
  Cancel,
  ApprovalOutlined,
  Task,
  KeyboardBackspace,
  WarningAmber,
  KeyboardArrowDown,
  Event,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { CrossIcon, DeleteIcon } from "lucide-react";
import Loader from "../../../../components/loader/loader";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import HistoryPrintDialog from "../../historyPrintDialog";
import { log } from "console";
import CultureTestForm from "../cultureResult";
import ReportPrintDialog from "../../reportPtrintDialog";
import dayjs from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

interface TestParameter {
  parameter: string;
  result: string | null;
  abnormal: boolean;
  interpretation: string;
  printOnReport: boolean;
  femaleRange?: string;
  unit?: string;
  comments?: string;
  patientValue?: string;
  // computationFormula
  computationFormula?: string;
  // parameterIdentifier for display only
  parameterIdentifier?: string;
  resultValues?: string[];
  /** Result field input mode: dropdown options default to text, else number */
  resultInputType?: "text" | "number";

  patientType?: {
    aboveMaxValueColor?: string;
    aboveMaxValueText?: string;
    belowMinValueColor?: string;
    belowMinValueText?: string;
    criticalMax?: string | number;
    criticalMin?: string | number;
    max?: string | number;
    min?: string | number;
    minMaxInWord?: string;
    noramalRangeColor?: string;
    noramalRangeText?: string;
    unit_cutOff?: string;
    patientTypeId?: string;
    parameterType?: string;
  };
  isNew?: boolean;
}
type IncomingTestResult = any;
interface cultureTestResults {
  medicineName: string;
  medicineGroup: string;
  sortOrder: number;
  sensitiveI: string;
  sensitiveII: string;
  sensitiveIII: string;
}
interface ChemicalExamination {
  specimen: string;
  ph: string;
  specificGravity: string;
  pusCells: string;
  epithelialCells: string;
  noOfPhrases: number;
  growthPattern: string;
  culturePhrases: string;
}

export enum InfoMode {
  PHONE_CALL = 'Phone Call',
  VERBALLY = 'Verbally',
  TEXT_MESSAGE = 'Text Message',
  WHATSAPP_TEXT = 'Whatsapp Text',
  EMAIL = 'Email'
}

const getDefaultResultInputType = (
  resultValues?: string[] | null
): "text" | "number" =>
  Array.isArray(resultValues) && resultValues.length > 0 ? "text" : "number";

/** Keep only a valid numeric draft (digits, one dot, optional leading minus). */
const sanitizeNumericResultInput = (value: string) => {
  let cleaned = String(value).replace(/[^\d.-]/g, "");
  const hasMinus = cleaned.startsWith("-");
  cleaned = cleaned.replace(/-/g, "");
  const parts = cleaned.split(".");
  cleaned =
    parts.length > 1
      ? `${parts[0]}.${parts.slice(1).join("")}`
      : parts[0] || "";
  if (hasMinus) cleaned = `-${cleaned}`;
  return cleaned;
};

const isNumericResultKeyAllowed = (key: string, currentValue: string) => {
  if (key.length !== 1) return true; // allow Enter, Backspace, arrows, etc. via other handlers
  if (key >= "0" && key <= "9") return true;
  if (key === "." && !currentValue.includes(".")) return true;
  if (key === "-" && currentValue.length === 0) return true;
  return false;
};

interface CriticalInfo {
  informedTo: string;
  date: string | dayjs.Dayjs;
  infoMode: string;
  comment: string;
  informedBy: string;
}

export default function LabResultEditor() {
  const location = useLocation();
  const navigate = useNavigate();

  const locState = (location.state as any) ?? {};
  // possible places the caller might put the id (currentId in your example)
  const sampleCollectionId =
    locState.sampleCollectionId ?? locState.currentId ?? null;
  // Keep some initial patient data from navigation state so UI doesn't flash empty while fetching
  const initialPatientData = locState.patientData ?? null;
  const initialPatientTypeName =
    locState.patientTypeName ??
    locState.patientTypeName ??
    locState.patientTypeData?.patientTypeName ??
    "";

  // local "data" shaped like your previous `remoteData` (so existing mapping code works)
  const [data, setData] = useState<any>(() => {
    // if navigation provided a full TestData object, use it immediately
    if (locState?.TestData) return locState;
    if (initialPatientData || initialPatientTypeName) {
      return {
        TestData: {
          patientData: initialPatientData ?? {},
          patientTypeData: { patientTypeName: initialPatientTypeName ?? "" },
          testResults: Array.isArray(locState?.testResults)
            ? locState.testResults
            : [],
        },
      };
    }
    return null;
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [testResultLoading, setTestResultLoading] = useState<boolean>(true);
  const [tabSwitchLoading, setTabSwitchLoading] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [openRefreshDialog, setOpenRefreshDialog] = useState<boolean>(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState<boolean>(false);
  const [previouseTests, setPreviouseTests] = useState<any[]>([]);
  const [openPrintDialog, setOpenPrintDialog] = useState<boolean>(false);
  const [selectedSampleId, setSelectedSampleId] = useState<string>("");
  const [testId, setTestId] = useState<string[]>([]);
  const [allReports, setAllReports] = useState<any[]>([]);
  const [openCriticalDialog, setOpenCriticalDialog] = useState<boolean>(false);
  const [criticalParameters, setCriticalParameters] = useState<any[]>([]);
  const [criticalInfo, setCriticalInfo] = useState<CriticalInfo>({
    informedTo: "",
    date: dayjs(),
    infoMode: InfoMode.PHONE_CALL,
    comment: "",
    informedBy: "",
  });
  const [openApprovedAllDialog, setOpenApprovedAllDialog] = useState<boolean>(false);

  // Store culture data per test ID to avoid sharing between tests
  const [cultureDataByTestId, setCultureDataByTestId] = useState<{
    [testId: string]: {
      cultureMedicines: string[];
      chemicalExamination: ChemicalExamination;
      cultureTestResults: cultureTestResults[];
    };
  }>({});

  const resultInputRefs = useRef<(HTMLDivElement | null)[]>([]);
  const saveButtonRef = useRef<HTMLButtonElement | null>(null);

  // Access control
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const accessConfigurations = profile?.data?.accessConfigurations || {};
  const labAccess = accessConfigurations?.Lab || {};

  useEffect(() => {
    if (profile?.data?.fullName) {
      setCriticalInfo(prev => ({ ...prev, informedBy: profile.data.fullName }));
    }
  }, [profile]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleConfirm = () => {
    handleApprove(); // call your approval function
    handleClose();
  };

  const handleApprovedAllConfirm = () => {
    handleApproveAllPerformedTests();
    setOpenApprovedAllDialog(false);
  };
  const loadTestsBySampleCollectionId = async () => {
    const id = String(sampleCollectionId);
    setLoading(true);
    const url = `${process.env.REACT_APP_LAB_URL}/patient-result/getTestsBySampleCollectionId/${id}`;
    const token = localStorage.getItem("token") ?? "";

    try {
      setTestResultLoading(true);
      const resp = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });
      const payload = resp.data ?? {};

      // Initialize culture data storage
      const newCultureDataByTestId: typeof cultureDataByTestId = {};

      const results = Array.isArray(payload.data) ? payload.data : [];

      // Process each test result and store culture data by test ID
      results.forEach((item: any) => {
        if (
          item.testResultType === "Culture Urine" ||
          item.testResultType === "Culture"
        ) {
          newCultureDataByTestId[item._id] = {
            cultureMedicines: item.cultureMedicines || [],
            chemicalExamination: item.chemicalExamination || {
              specimen: "",
              ph: "",
              specificGravity: "",
              pusCells: "",
              epithelialCells: "",
              noOfPhrases: 0,
              growthPattern: "",
              culturePhrases: "",
            },
            cultureTestResults: item.cultureMedicines || [],
          };
        }
      });

      setCultureDataByTestId(newCultureDataByTestId);

      if (results.length === 0) {
        setData(null);
        return;
      }

      const newData = {
        TestData: {
          patientData: initialPatientData ?? {},
          patientTypeData: { patientTypeName: initialPatientTypeName ?? "" },
          testResults: results,
        },
      };

      setData(newData);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      if (axios.isCancel(err)) return;
      toast.error(err?.message || "Failed to fetch test results.");
      setData(null);

    } finally {
    }
  };

  const getPreviousTests = async () => {
    const id = String(sampleCollectionId);
    const url = `${process.env.REACT_APP_LAB_URL}/patient-result/getPatientPreviousHistory/${id}`;
    const token = localStorage.getItem("token") ?? "";

    try {
      const resp = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });

      if (resp.data.isSuccess === true) {
        setPreviouseTests(resp.data.data);
        setOpenHistoryDialog(true);
        // toast.success(resp.data.message);
      }
    } catch (err: any) {
      if (axios.isCancel(err)) return;
      toast.error(err?.message || "Failed to fetch previous tests.");
      setData(null);
    }
  };

  // call the function from useEffect and cancel on cleanup
  useEffect(() => {
    if (!sampleCollectionId) return;

    const source = axios.CancelToken.source();
    loadTestsBySampleCollectionId();

    return () => {
      source.cancel("Component unmounted - request cancelled");
    };
  }, [sampleCollectionId]);

  const fallback = {
    TestData: {
      patientData: {
        patientName: "",
        guardiansName: "",
        phonNumber: "",
        dob: "",
        gender: "",
      },
      patientTypeData: { patientTypeName: "" },
      testResults: [],
    },
  };

  const effectiveData = data ?? fallback;

  // map incomin  g testResults -> UI-friendly structure (same as before)
  const mappedInitialResults = useMemo(() => {
    const results: IncomingTestResult[] = Array.isArray(
      effectiveData?.TestData?.testResults
    )
      ? effectiveData.TestData.testResults
      : [];

    return results.map(tr => {
      const paramsSrc = Array.isArray(tr.testParameterToPerform)
        ? tr.testParameterToPerform
        : [];
      const mappedParams: TestParameter[] = paramsSrc.map((p: any) => ({
        parameter: p.parameter ?? "Unknown",
        result: p.result ?? null,
        abnormal: !!(p.isAbnormal ?? true),
        printOnReport:
          typeof p.printOnReport === "boolean"
            ? p.printOnReport
            : !!(p.print ?? true),
        femaleRange: p.normalValues ?? "",
        unit: p.units ?? "",
        comments: p.comments,
        interpretation: p.interpretation || "",
        resultValues: p.resultValues || [],
        resultInputType: getDefaultResultInputType(p.resultValues || []),
        patientType: p.patientType,
        patientValue: p.patientValue ?? "",
        computationFormula: p.computationFormula ?? "",
        parameterIdentifier: p.parameterIdentifier ?? "",
        isNew: false,
      }));

      return { ...tr, testParameterToPerform: mappedParams };
    });
  }, [effectiveData]);

  const currentId = (locState as any)?.currentId ?? null;
  const initialIndex = useMemo(() => {
    if (!currentId) return 0;
    const idx = mappedInitialResults.findIndex(tr => tr._id === currentId);
    return idx !== -1 ? idx : 0;
  }, [mappedInitialResults, currentId]);

  const [activeIndex, setActiveIndex] = useState(initialIndex);

  // make sure it also updates when data/currentId changes
  useEffect(() => {
    setActiveIndex(initialIndex);
  }, [initialIndex]);

  const [testResultsState, setTestResultsState] = useState<
    IncomingTestResult[]
  >([]);

  useEffect(() => {
    setTestResultsState(mappedInitialResults);
  }, [mappedInitialResults]);

  const activeTest = useMemo(
    () => testResultsState[activeIndex] ?? null,
    [testResultsState, activeIndex]
  );

  // Helper functions to get culture data for the current active test
  const getCurrentCultureData = useCallback(() => {
    const activeTestId = activeTest?._id;
    if (!activeTestId) return null;
    return cultureDataByTestId[activeTestId] || null;
  }, [activeTest?._id, cultureDataByTestId]);

  const updateCurrentCultureData = useCallback(
    (updates: Partial<(typeof cultureDataByTestId)[string]>) => {
      const activeTestId = activeTest?._id;
      if (!activeTestId) return;

      const defaultCultureData = {
        cultureMedicines: [],
        chemicalExamination: {
          specimen: "",
          ph: "",
          specificGravity: "",
          pusCells: "",
          epithelialCells: "",
          noOfPhrases: 0,
          growthPattern: "",
          culturePhrases: "",
        },
        cultureTestResults: [],
      };

      setCultureDataByTestId(prev => ({
        ...prev,
        [activeTestId]: {
          ...defaultCultureData,
          ...prev[activeTestId],
          ...updates,
        },
      }));
    },
    [activeTest?._id]
  );

  // Get current test's culture data
  const currentCultureData = useMemo(
    () => getCurrentCultureData(),
    [getCurrentCultureData]
  );

  const cultureMedicines = useMemo(
    () => currentCultureData?.cultureMedicines || [],
    [currentCultureData?.cultureMedicines]
  );

  const chemicalExamination = useMemo(
    () =>
      currentCultureData?.chemicalExamination || {
        specimen: "",
        ph: "",
        specificGravity: "",
        pusCells: "",
        epithelialCells: "",
        noOfPhrases: 0,
        growthPattern: "",
        culturePhrases: "",
      },
    [currentCultureData?.chemicalExamination]
  );

  const cultureTestResults = useMemo(
    () => currentCultureData?.cultureTestResults || [],
    [currentCultureData?.cultureTestResults]
  );

  // Memoize callback functions for CultureTestForm
  const handleCultureTestResultsUpdate = useCallback(
    (results: any[]) => {
      updateCurrentCultureData({ cultureTestResults: results });
    },
    [updateCurrentCultureData]
  );

  const handleChemicalExaminationUpdate = useCallback(
    (examination: any) => {
      updateCurrentCultureData({ chemicalExamination: examination });
    },
    [updateCurrentCultureData]
  );

  const currentParameters: TestParameter[] = useMemo(
    () => (activeTest?.testParameterToPerform as TestParameter[]) ?? [],
    [activeTest?.testParameterToPerform]
  );

  const statusColor = (status?: string) => {
    if (!status) return "#6b7280";
    const s = status.toLowerCase();
    if (s.includes("perform")) return "#ef4444";
    if (s.includes("approval") && s.includes("pending")) return "#f59e0b";
    if (s.includes("approved")) return "#10b981";
    return "#6b7280";
  };

  // Optimize refs array resizing - only resize when length actually changes
  useEffect(() => {
    if (resultInputRefs.current.length !== currentParameters.length) {
      resultInputRefs.current = resultInputRefs.current.slice(
        0,
        currentParameters.length
      );
    }
  }, [currentParameters.length]); // Only depend on length, not the entire array

  const patientData = effectiveData?.TestData?.patientData ?? {};
  const patientType =
    effectiveData?.TestData?.patientTypeData?.patientTypeName ??
    effectiveData?.TestData?.patientTypeName ??
    "";
  const collectionCenter = activeTest?.collectionCenter ?? {};


  const handleResultKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Stop the default 'Enter' behavior (like form submission)

      const nextIndex = index + 1;

      // Check if there is a next result input to focus on
      if (nextIndex < currentParameters.length) {
        const nextInputRef = resultInputRefs.current[nextIndex];
        if (nextInputRef) {
          // The ref is on the TextField, we need to find the actual <input> inside it
          const inputElement = nextInputRef.querySelector("input");
          inputElement?.focus();
        }
      } else {
        // If we're on the last input, focus the save button
        saveButtonRef.current?.focus();
      }
    }
  };

  const parseRangeValue = (
    value: string | number | undefined | null
  ): number | null => {
    if (value == null) return null;

    // If it's already a number, return it directly
    if (typeof value === "number") {
      return isNaN(value) ? null : value;
    }

    // Attempt to extract the first number from the string
    const match = String(value).match(/(\d+(\.\d+)?)/);
    if (match) {
      const parsed = parseFloat(match[0]);
      return isNaN(parsed) ? null : parsed;
    }
    // If no number is found, treat it as null for range calculation
    return null;
  };

  // Function to calculate result based on C-Formula
  const calculateFormulaResult = (
    formula: string,
    parameters: TestParameter[]
  ): string => {
    if (!formula || formula.trim() === "") return "";

    try {
      let calculatedFormula = formula.trim();

      // Replace parameter identifiers with their result values
      parameters.forEach(param => {
        if (
          param.parameterIdentifier &&
          param.result !== null &&
          param.result !== ""
        ) {
          const identifier = param.parameterIdentifier.trim();
          const resultValue = String(param.result).trim();
          const result = parseFloat(resultValue);

          if (!isNaN(result) && identifier) {
            // Create a more precise regex that matches whole words
            const regex = new RegExp(
              `\\b${identifier.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
              "g"
            );
            calculatedFormula = calculatedFormula.replace(
              regex,
              result.toString()
            );
          }
        }
      });

      // Convert ^ (caret) to ** (JavaScript exponentiation operator)
      // Handle cases like (ab+ca)^2, x^3, etc.
      calculatedFormula = calculatedFormula.replace(/\^/g, "**");

      // Remove any remaining alphabetic characters that weren't replaced (invalid identifiers)
      const cleanFormula = calculatedFormula.replace(
        /[a-zA-Z_][a-zA-Z0-9_]*/g,
        "0"
      );

      // Validate that only allowed characters remain (now including ** for exponentiation)
      const allowedChars = /^[0-9+\-*/().\s*]+$/;
      if (!allowedChars.test(cleanFormula)) {
        console.error(
          "Formula contains invalid characters after cleaning:",
          cleanFormula
        );
        return "";
      }

      // Additional safety check - ensure parentheses are balanced
      const openParens = (cleanFormula.match(/\(/g) || []).length;
      const closeParens = (cleanFormula.match(/\)/g) || []).length;
      if (openParens !== closeParens) {
        console.error("Unbalanced parentheses in formula:", cleanFormula);
        return "";
      }

      // Evaluate the mathematical expression
      let result;
      try {
        // Use Function constructor for safer evaluation than eval
        result = new Function(`"use strict"; return (${cleanFormula})`)();
      } catch (evalError) {
        console.error("Error evaluating expression:", evalError);
        return "";
      }

      if (typeof result === "number" && !isNaN(result) && isFinite(result)) {
        // Check if any parameter in the formula has points/score units for rounding decision
        const hasPointsUnit = parameters.some(
          param =>
            param.unit?.toLowerCase().includes("point") ||
            param.unit?.toLowerCase().includes("score") ||
            param.unit?.toLowerCase().includes("pts")
        );

        // Round to whole number if dealing with points/scores, otherwise keep 4 decimal places
        const rounded = hasPointsUnit
          ? Math.round(result)
          : parseFloat(result.toFixed(0));
        return rounded.toString();
      }

      console.error("Invalid calculation result:", result);
      return "";
    } catch (error) {
      console.error("Error calculating formula:", error);
      return "";
    }
  };

  const computeFlagsFromRange = (
    min: number | undefined | null,
    max: number | undefined | null,
    resultRaw: string | null,
    rangeString: string | null | undefined
  ) => {
    if (resultRaw == null || String(resultRaw).trim() === "") {
      return { abnormal: false, printOnReport: false };
    }

    let abnormal = true;
    let printOnReport = true;

    const r = parseFloat(String(resultRaw).replace(/[, ]+/g, ""));
    if (Number.isNaN(r)) {
      const matches = rangeString && String(resultRaw).toLowerCase().trim() === rangeString.toLowerCase().trim();
      return { abnormal: !matches, printOnReport };
    }

    // if both min & max available
    if (min != null && max != null) {
      abnormal = !(r >= min && r <= max);
      printOnReport = true;
      return { abnormal, printOnReport };
    }

    // if only max is available
    if (max != null) {
      abnormal = !(r < max);
      printOnReport = true;
      return { abnormal, printOnReport };
    }

    // if only min is available
    if (min != null) {
      abnormal = !(r > min);
      printOnReport = true;
      return { abnormal, printOnReport };
    }

    // no range info
    return { abnormal: false, printOnReport: true };
  };

  const updateParameter = (
    testIdx: number,
    paramIdx: number,
    field: keyof TestParameter,
    value: any
  ) => {
    setTestResultsState(prev =>
      prev.map((tr, i) => {
        if (i !== testIdx) return tr;
        const params = (tr.testParameterToPerform as TestParameter[]).map(
          (p, j) => {
            if (j !== paramIdx) return p;
            let updated = { ...p, [field]: value };
            // Round result if it's in points and field is result
            if (field === "result" && value !== null && value !== "") {
              const unit = updated.unit?.toLowerCase() || "";
              if (
                unit.includes("point") ||
                unit.includes("score") ||
                unit.includes("pts")
              ) {
                const numericValue = parseFloat(String(value));
                if (!isNaN(numericValue)) {
                  updated = {
                    ...updated,
                    result: Math.round(numericValue).toString(),
                  };
                }
              }
            }

            // This logic now handles both API-driven and custom ranges
            if (
              field === "result" ||
              (field === "femaleRange" && updated.isNew)
            ) {
              let minNum: number | null = null;
              let maxNum: number | null = null;

              const patientTypeRangeExists =
                updated.patientType &&
                (updated.patientType.min != null ||
                  updated.patientType.max != null);

              // --- PRIORITY 1: Check for structured data from the API ---
              if (patientTypeRangeExists) {
                minNum = parseRangeValue(updated.patientType!.min);
                maxNum = parseRangeValue(updated.patientType!.max);
              }
              // --- PRIORITY 2: Fallback to parsing the femaleRange string (for custom params) ---
              else if (
                typeof updated.femaleRange === "string" &&
                updated.femaleRange.includes("-")
              ) {
                const parts = updated.femaleRange.split("-");
                minNum = parseRangeValue(parts[0]);
                maxNum = parseRangeValue(parts[1]);
              }

              // Now, calculate the flags with the best available range data
              const { abnormal, printOnReport } = computeFlagsFromRange(
                minNum,
                maxNum,
                updated.result,
                updated.femaleRange
              );

              updated.abnormal = abnormal;
              updated.printOnReport = printOnReport;
            }
            return updated;
          }
        );

        // If result was updated, recalculate OTHER parameters with formulas (not the one being manually entered)
        if (field === "result") {
          params.forEach((param, index) => {
            if (
              index !== paramIdx && // Don't override the parameter being manually entered
              param.computationFormula &&
              param.computationFormula.trim() !== ""
            ) {
              const calculatedResult = calculateFormulaResult(
                param.computationFormula,
                params
              );
              if (calculatedResult !== "") {
                params[index] = { ...param, result: calculatedResult };

                // Recalculate range flags for calculated result
                let minNum: number | null = null;
                let maxNum: number | null = null;

                const patientTypeRangeExists =
                  param.patientType &&
                  (param.patientType.min != null ||
                    param.patientType.max != null);

                if (patientTypeRangeExists) {
                  minNum = parseRangeValue(param.patientType!.min);
                  maxNum = parseRangeValue(param.patientType!.max);
                } else if (
                  typeof param.femaleRange === "string" &&
                  param.femaleRange.includes("-")
                ) {
                  const parts = param.femaleRange.split("-");
                  minNum = parseRangeValue(parts[0]);
                  maxNum = parseRangeValue(parts[1]);
                }

                const { abnormal, printOnReport } = computeFlagsFromRange(
                  minNum,
                  maxNum,
                  calculatedResult,
                  param.femaleRange
                );

                params[index].abnormal = abnormal;
                params[index].printOnReport = printOnReport;
              }
            }
          });
        }

        return { ...tr, testParameterToPerform: params };
      })
    );
  };

  // Remove artificial delay - set loading to false immediately after data processing
  useEffect(() => {
    if (testResultsState.length > 0) {
      setTestResultLoading(false);
    }
  }, [testResultsState]);

  // Calculate formulas on initial load only - prevent overwriting manual entries
  useEffect(() => {
    if (testResultsState.length > 0) {
      setTestResultsState(prev =>
        prev.map(tr => {
          const params = [...(tr.testParameterToPerform as TestParameter[])];
          let hasChanges = false;

          params.forEach((param, index) => {
            if (
              param.computationFormula &&
              param.computationFormula.trim() !== "" &&
              (!param.result || param.result === "") // Only calculate if result is empty
            ) {
              const calculatedResult = calculateFormulaResult(
                param.computationFormula,
                params
              );
              if (calculatedResult !== "") {
                params[index] = { ...param, result: calculatedResult };
                hasChanges = true;

                // Recalculate range flags for calculated result
                let minNum: number | null = null;
                let maxNum: number | null = null;

                const patientTypeRangeExists =
                  param.patientType &&
                  (param.patientType.min != null ||
                    param.patientType.max != null);

                if (patientTypeRangeExists) {
                  minNum = parseRangeValue(param.patientType!.min);
                  maxNum = parseRangeValue(param.patientType!.max);
                } else if (
                  typeof param.femaleRange === "string" &&
                  param.femaleRange.includes("-")
                ) {
                  const parts = param.femaleRange.split("-");
                  minNum = parseRangeValue(parts[0]);
                  maxNum = parseRangeValue(parts[1]);
                }

                const { abnormal, printOnReport } = computeFlagsFromRange(
                  minNum,
                  maxNum,
                  calculatedResult,
                  param.femaleRange
                );

                params[index].abnormal = abnormal;
                params[index].printOnReport = printOnReport;
              }
            }
          });

          return hasChanges ? { ...tr, testParameterToPerform: params } : tr;
        })
      );
    }
  }, [mappedInitialResults.length]); // Only run when initial data length changes (initial load)

  const addParameter = (testIdx: number) => {
    const newParam: TestParameter = {
      parameter: "",
      result: null,
      abnormal: false,
      printOnReport: false,
      interpretation: "",
      resultValues: [],
      resultInputType: "number",
      femaleRange: "",
      unit: "",
      comments: "",
      patientValue: "",
      computationFormula: "",
      parameterIdentifier: "",
      isNew: true,
    };

    setTestResultsState(prev =>
      prev.map((tr, i) => {
        if (i !== testIdx) return tr;
        const existing = Array.isArray(tr.testParameterToPerform)
          ? tr.testParameterToPerform
          : [];
        return { ...tr, testParameterToPerform: [...existing, newParam] };
      })
    );
    setActiveIndex(testIdx);
  };
  const removeParameter = (testIdx: number, paramIdx: number) => {
    setTestResultsState(prev =>
      prev.map((tr, i) => {
        if (i !== testIdx) return tr;
        const existing = Array.isArray(tr.testParameterToPerform)
          ? [...tr.testParameterToPerform]
          : [];

        // safety: if index out of range -> return unchanged
        if (paramIdx < 0 || paramIdx >= existing.length) return tr;

        // only allow removal for custom parameters (isNew === true)
        if (!existing[paramIdx]?.isNew) {
          toast.info("Only custom parameters can be removed.");
          return tr;
        }

        // remove the custom parameter
        existing.splice(paramIdx, 1);
        return { ...tr, testParameterToPerform: existing };
      })
    );

    // keep activeIndex as-is; no navigation needed
  };
  const activeApprovalStatus = useMemo(
    () => testResultsState[activeIndex]?.approvalStatus ?? "",
    [testResultsState, activeIndex]
  );
  const approvedByDoctor = useMemo(
    () => testResultsState[activeIndex]?.approvedBy,
    [testResultsState, activeIndex]
  );
  const indicatorColor = useMemo(
    () => statusColor(activeApprovalStatus),
    [activeApprovalStatus]
  );

  // Memoize test type checks to prevent repeated computations
  const isCultureTest = useMemo(
    () =>
      activeTest?.testResultType?.includes("Culture Urine") ||
      activeTest?.testResultType?.includes("Culture"),
    [activeTest?.testResultType]
  );

  const isPCRTest = useMemo(
    () => activeTest?.testResultType?.includes("PCR's special test"),
    [activeTest?.testResultType]
  );

  const isParameterizedTest = useMemo(
    () => !isPCRTest && !isCultureTest,
    [isPCRTest, isCultureTest]
  );

  // Initialize culture data for active test if it's a culture test and doesn't have data yet
  useEffect(() => {
    if (
      isCultureTest &&
      activeTest?._id &&
      !cultureDataByTestId[activeTest._id]
    ) {
      setCultureDataByTestId(prev => ({
        ...prev,
        [activeTest._id]: {
          cultureMedicines: [],
          chemicalExamination: {
            specimen: "",
            ph: "",
            specificGravity: "",
            pusCells: "",
            epithelialCells: "",
            noOfPhrases: 0,
            growthPattern: "",
            culturePhrases: "",
          },
          cultureTestResults: [],
        },
      }));
    }
  }, [isCultureTest, activeTest?._id]);

  const handleSave = async () => {
    if (!activeTest) {
      toast.error("No active test selected.");
      return;
    }

    // Check for critical values before saving
    const criticalParams = checkCriticalValues();
    if (criticalParams.length > 0) {
      setCriticalParameters(criticalParams);
      setOpenCriticalDialog(true);
      return;
    }

    // Proceed with save if no critical values
    await performSave();
  };

  const performSave = async () => {
    if (!activeTest) {
      toast.error("No active test selected.");
      return;
    }

    const pathId = activeTest._id ?? "";
    if (!pathId) {
      toast.info("Cannot determine test id.");
      return;
    }

    const testId = activeTest.testId ?? "";
    const sampleCollectionIdForApi = activeTest.sampleCollectionId ?? "";
    const patientId = activeTest.patientId ?? "";

    const topLevelComment = activeTest.comments ?? "";
    const notes = activeTest.notes ?? "";
    const method = activeTest.method ?? "";
    const resultValue = activeTest.resultValue || "";
    const viralLoad = activeTest.viralLoad || "";

    const mappedParams =
      (activeTest.testParameterToPerform as TestParameter[] | undefined) ?? [];

    // Debug: Log parameters before saving
    const apiParams = mappedParams.map(p => ({
      parameter: p.parameter ?? "",
      result: p.result == null ? "" : String(p.result),
      normalValues: p.femaleRange ?? "",
      units: p.unit ?? "",
      comments: p.comments || "",
      isAbnormal: !!p.abnormal,
      printOnReport: !!p.printOnReport,
      interpretation: p.interpretation || "",
      patientType: p.patientType,
      ...(p.patientValue !== undefined &&
        p.patientValue !== "" && { patientValue: p.patientValue }),
      resultValues: p.resultValues || [],
      ...(p.parameterIdentifier !== undefined &&
        p.parameterIdentifier !== "" && {
        parameterIdentifier: p.parameterIdentifier,
      }),
      ...(p.computationFormula !== undefined &&
        p.computationFormula !== "" && {
        computationFormula: p.computationFormula,
      }),
    }));

    const payload = {
      testId: String(testId),
      sampleCollectionId: String(sampleCollectionIdForApi),
      patientId: String(patientId),
      comments: topLevelComment,
      notes,
      method,
      viralLoad,
      resultValue,
      performedOn: activeTest?.performedOn,
      testParameterToPerform: apiParams,
      ...(isCultureTest && {
        chemicalExamination: currentCultureData?.chemicalExamination,
      }),
      ...(isCultureTest && {
        cultureMedicines: currentCultureData?.cultureTestResults,
      }),
      ...(criticalParameters.length > 0 && {
        criticalInfo: {
          ...criticalInfo,
          date: dayjs(criticalInfo.date).toISOString(),
        },
      }),
    };

    try {
      const url = `${process.env.REACT_APP_LAB_URL}/patient-result/performTestResultById/${pathId}`;
      const resp = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        toast.error(`Save failed: ${resp.status} ${resp.statusText}`);
        return;
      }

      const json = await resp.json();
      if (json.isSuccess) {
        toast.success(json.message);
        loadTestsBySampleCollectionId();
        if (activeIndex < testResultsState.length - 1) {
          setActiveIndex(prev => prev + 1);
        } else {
          navigate(-1);
        }
      }
    } catch (err) {
      toast.error("Save failed.");
      console.error("Save exception:", err);
    }
  };

  const handleCriticalDialogProceed = () => {
    if (!criticalInfo.informedTo || !criticalInfo.informedBy || !criticalInfo.infoMode) {
      toast.error("Please fill all required critical information fields.");
      return;
    }
    setOpenCriticalDialog(false);
    performSave();
  };

  const handleCriticalDialogCancel = () => {
    setOpenCriticalDialog(false);
    setCriticalParameters([]);
    setCriticalInfo({
      informedTo: "",
      date: dayjs(),
      infoMode: InfoMode.PHONE_CALL,
      comment: "",
      informedBy: profile?.data?.fullName || "",
    });
  };

  // Function to check critical values
  const checkCriticalValues = () => {
    if (!activeTest) return [];

    const mappedParams =
      (activeTest.testParameterToPerform as TestParameter[] | undefined) ?? [];

    const criticalParams = mappedParams.filter(param => {
      if (!param.result || param.result === null || param.result === "") {
        return false;
      }

      const resultValue = parseFloat(
        String(param.result).replace(/[, ]+/g, "")
      );
      if (isNaN(resultValue)) return false;

      const patientType = param.patientType;
      if (!patientType) return false;

      const criticalMin = patientType.criticalMin
        ? parseFloat(String(patientType.criticalMin))
        : null;
      const criticalMax = patientType.criticalMax
        ? parseFloat(String(patientType.criticalMax))
        : null;

      // Check if result is outside critical range
      const isBelowCriticalMin =
        criticalMin !== null && resultValue < criticalMin;
      const isAboveCriticalMax =
        criticalMax !== null && resultValue > criticalMax;

      return isBelowCriticalMin || isAboveCriticalMax;
    });

    return criticalParams.map(param => {
      const resultValue = parseFloat(
        String(param.result).replace(/[, ]+/g, "")
      );
      const criticalMin = param.patientType?.criticalMin
        ? parseFloat(String(param.patientType.criticalMin))
        : null;
      const criticalMax = param.patientType?.criticalMax
        ? parseFloat(String(param.patientType.criticalMax))
        : null;

      let criticalType = "";
      if (criticalMin !== null && resultValue < criticalMin) {
        criticalType = "Below Critical Minimum";
      } else if (criticalMax !== null && resultValue > criticalMax) {
        criticalType = "Above Critical Maximum";
      }

      return {
        parameter: param.parameter,
        result: param.result,
        unit: param.unit || "",
        criticalMin: criticalMin,
        criticalMax: criticalMax,
        criticalType: criticalType,
      };
    });
  };

  const handleApprove = async () => {
    try {
      if (!activeApprovalStatus.includes("Approval")) {
        toast.info("Please take Approval First");
        return;
      }
      if (!activeTest) {
        toast.error("No active test selected.");
        return;
      }

      const pathId = activeTest._id ?? "";
      if (!pathId) {
        toast.info("Cannot determine test id.");
        return;
      }

      const url = `${process.env.REACT_APP_LAB_URL}/patient-result/approvedTestResultById/${pathId}`;
      const token = localStorage.getItem("token");
      const response = await axios.put(
        url,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.data.isSuccess) {
        toast.success(response?.data.message || "Test Approved Successfully");
        loadTestsBySampleCollectionId();
      }
    } catch (error: any) {
      toast.error(error?.response.message || "Error Occuerred");
    }
  };

  const handleApproveAllPerformedTests = async () => {
    try {
      if (!sampleCollectionId) {
        toast.error("No sample collection ID available.");
        return;
      }

      const url = `${process.env.REACT_APP_LAB_URL}/patient-result/approvedAllPerformedTest/${sampleCollectionId}`;
      const token = localStorage.getItem("token");
      const response = await axios.put(
        url,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        } 
      );

      if (response?.data.isSuccess) {
        toast.success(response?.data.message || "All Performed Tests Approved Successfully");
        loadTestsBySampleCollectionId();
      }else{
        toast.error(response?.data.message || "Failed to Approve All Performed Tests");
      }
    } catch (error: any) {
      toast.error(error?.response?.message || "Error Occurred");
    }
  };

  // Move reperform to component scope so it can be used anywhere
  const reperform = async () => {
    try {
      const tr = testResultsState[activeIndex];
      if (!tr || !tr._id) {
        toast.error("No active test selected for reperform.");
        return;
      }
      const url = `${process.env.REACT_APP_LAB_URL}/patient-result/reperformTestById/${tr._id}`;
      const token = localStorage.getItem("token");
      const response = await axios.put(
        url,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.data.isSuccess) {
        toast.success(response?.data.message || "Test Repeated Successfully");
        loadTestsBySampleCollectionId();
      }
    } catch (error: any) {
      toast.error(error?.response?.message || "Error Occuerred");
    }
  };
  // const option =
  //   data?.TestData.testResults[0]?.testParameterToPerform[0]?.resultValues ??
  //   [];

  const renderTabLabel = useCallback((tr: any, idx: number) => {
    const approvalStatus = tr.approvalStatus ?? tr.status ?? "";
    const color = statusColor(approvalStatus);

    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
        {/* color dot */}
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: color,
            flexShrink: 0,
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        />

        {/* label text in black */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}
          >
            {`${tr.testCode ?? ""} - ${tr.testName ?? ""}`}
          </Typography>
        </Box>
      </Box>
    );
  }, []);

  // if(testResultLoading){
  //   return <div className="flex justify-center items-center h-[90vh]">
  //     <Loader />
  //   </div>;
  // }

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        backgroundColor: "white",
      }}
    >
      {/* Back button (left) + Lab Information Section (right) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 1.2,
          mb: 1,
        }}
      >
        <Tooltip title="Back To Test-Result">
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<KeyboardBackspace />}
              onClick={() => navigate(-1)}
              sx={{ textTransform: "none" }}
            >
              Back
            </Button>
          </span>
        </Tooltip>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Lab Information
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.2,
            }}
          >
            <TextField
              size="small"
              className="custom-input"
              label="Lab Name"
              value={collectionCenter.collectionCenterName ?? ""}
              InputProps={{ readOnly: true }}
              sx={{ width: { xs: "100%", sm: 200 } }}
            />
            <TextField
              size="small"
              className="custom-input"
              label="Lab Address"
              value={collectionCenter.address ?? ""}
              InputProps={{ readOnly: true }}
              sx={{ width: { xs: "100%", sm: 200 } }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.2,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" fontWeight={600}>
            Patient Information
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(6, 1fr)",
          },
          gap: 1.2,
          mb: 1,
        }}
      >
        <TextField
          size="small"
          className="custom-input"
          label="Patient Name"
          value={patientData.patientName ?? ""}
          InputProps={{ readOnly: true }}
        />
        <TextField
          size="small"
          className="custom-input"
          label="Guardian"
          value={patientData.guardiansName ?? ""}
          InputProps={{ readOnly: true }}
        />
        <TextField
          size="small"
          className="custom-input"
          label="Phone"
          value={patientData.phonNumber ?? ""}
          InputProps={{ readOnly: true }}
        />
        <TextField
          size="small"
          className="custom-input"
          label="DOB"
          value={patientData.dob ?? ""}
          InputProps={{ readOnly: true }}
        />
        <TextField
          size="small"
          className="custom-input"
          label="Gender"
          value={patientData.gender ?? ""}
          InputProps={{ readOnly: true }}
        />
        <TextField
          size="small"
          className="custom-input"
          label="Patient Type"
          value={patientType ?? ""}
          InputProps={{ readOnly: true }}
        />
      </Box>

      {loading ? (
        <Loader />
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
            <Tabs
              value={activeIndex}
              onChange={(_, v) => {
                // Optimize tab switching - only update if different
                if (v !== activeIndex) {
                  setTabSwitchLoading(true);
                  setActiveIndex(v);

                  setTimeout(() => setTabSwitchLoading(false), 500);
                }
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 36,
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: 2,
                  backgroundColor: `${indicatorColor}`,
                },
                // still keep Tab defaults slim
                "& .MuiTab-root": {
                  minHeight: 32,
                  py: 0.5,
                  px: 1,
                  textTransform: "none",
                },
              }}
            >
              {testResultsState.map((tr, idx) => (
                <Tab
                  key={tr._id ?? idx}
                  label={renderTabLabel(tr, idx)}
                  sx={{
                    // bordered "card" look for each tab
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    mr: 1,
                    minHeight: 40,
                    py: 0.5,
                    px: 1,
                    textTransform: "none",
                    backgroundColor: `background.paper`,

                    // ensure the inner wrapper aligns nicely
                    "& .MuiTab-wrapper": {
                      gap: 8,
                      alignItems: "center",
                      justifyContent: "flex-start",
                    },

                    // selected state: emphasize border with indicatorColor
                    "&.Mui-selected": {
                      borderColor: indicatorColor,
                      backgroundColor: `${indicatorColor}20`,
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              flexDirection: "column",
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                width: "100%",
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Test Details
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", flexDirection: "row", gap: 0.5, ml: 1 }}
              >
                <div
                  className="flex gap-2"
                  style={{ fontSize: "0.6rem" }} // sets base font size for all children
                >
                  <Tooltip
                    placement="top"
                    arrow
                    title={
                      !labAccess?.can_see_history
                        ? "No access to see history"
                        : "History"
                    }
                  >
                    <span>
                      <Button
                        onClick={() => {
                          getPreviousTests();
                        }}
                        variant="outlined"
                        size="small"
                        disabled={!labAccess?.can_see_history}
                        startIcon={<History sx={{ fontSize: "0.6rem" }} />}
                        sx={{
                          fontSize: "0.6rem",
                          minWidth: "auto",
                          padding: "2px 6px",
                          color: labAccess?.can_see_history
                            ? "inherit"
                            : "#ccc",
                          borderColor: labAccess?.can_see_history
                            ? "inherit"
                            : "#ccc",
                          cursor: labAccess?.can_see_history
                            ? "pointer"
                            : "not-allowed",
                        }}
                      >
                        History
                      </Button>
                    </span>
                  </Tooltip>

                  <Tooltip
                    placement="top"
                    arrow
                    title={
                      !labAccess?.can_reperform_test
                        ? "No access to reperform test"
                        : "Reperform"
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setOpenRefreshDialog(true);
                        }}
                        size="small"
                        disabled={!labAccess?.can_reperform_test}
                        startIcon={<Replay sx={{ fontSize: "0.6rem" }} />}
                        sx={{
                          fontSize: "0.6rem",
                          minWidth: "auto",
                          padding: "2px 6px",
                          color: labAccess?.can_reperform_test
                            ? "inherit"
                            : "#ccc",
                          borderColor: labAccess?.can_reperform_test
                            ? "inherit"
                            : "#ccc",
                          cursor: labAccess?.can_reperform_test
                            ? "pointer"
                            : "not-allowed",
                        }}
                      >
                        Reperform
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={1.2}>
                  <Grid item xs={6} sm={2.5}>
                    <TextField
                      size="small"
                      className="custom-input"
                      label="Test Name"
                      value={activeTest?.testName ?? ""}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      size="small"
                      className="custom-input"
                      label="Test Code"
                      value={activeTest?.testCode ?? activeTest?.testId ?? ""}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={2.5}>
                    <TextField
                      size="small"
                      className="custom-input"
                      label="Status"
                      value={activeTest?.approvalStatus ?? "-"}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <DateTimePicker
                      label="Performed On"
                      value={activeTest?.performedOn ? dayjs(activeTest.performedOn) : dayjs()}
                      onChange={(newValue) => {
                        const dateStr = newValue ? newValue.toISOString() : dayjs().toISOString();
                        setTestResultsState(prev => prev.map((tr, i) => i === activeIndex ? { ...tr, performedOn: dateStr } : tr));
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          className: 'custom-input',
                          fullWidth: true,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      size="small"
                      className="custom-input"
                      label="Performed By"
                      value={
                        activeTest?.performedBy ??
                        activeTest?.performedByName ??
                        "-"
                      }
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </Box>
          </Box>

          {testResultLoading || tabSwitchLoading ? (
            <div className="flex justify-center items-center h-[10vh]">
              <Loader />
            </div>
          ) : (
            <div>
              {isCultureTest && (
                <Box sx={{ mb: 2 }}>
                  {/* Use React.Suspense for lazy loading if needed */}
                  <CultureTestForm
                    testName={activeTest?.testResultType}
                    cultureMedicines={cultureMedicines}
                    cultureTestResults={cultureTestResults}
                    setCultureTestResults={handleCultureTestResultsUpdate}
                    chemicalExamination={chemicalExamination}
                    setChemicalExamination={handleChemicalExaminationUpdate}
                    index={activeIndex}
                  />
                </Box>
              )}

              {isParameterizedTest ? (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Test Parameters / {""}
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => addParameter(activeIndex)}
                          disabled={!activeTest}
                          className="custom-input"
                          sx={{ fontSize: ".6rem" }}
                        >
                          Add Custom Parameter
                        </Button>
                      </span>
                    </Typography>
                  </Box>

                  <TableContainer
                    component={Paper}
                    sx={{
                      boxShadow: "none",
                      backgroundColor: "#F3F6FB",
                      borderRadius: 1,
                      overflow: "auto",
                    }}
                  >
                    <Table
                      size="small"
                      stickyHeader
                      sx={{
                        "& .MuiTableCell-stickyHeader": {
                          backgroundColor: "#B4DAF6",
                          color: "black",
                          fontWeight: 500,
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 0.25, px: 0.5 }}>
                            Parameter
                          </TableCell>
                          <TableCell sx={{ py: 0.25, px: 0.5 }}>
                            Result
                          </TableCell>
                          {activeTest?.testResultType === "Parameterized" &&
                            activeTest?.testResultTemplate ===
                            "Test Result with Patient Values" && (
                              <TableCell sx={{ py: 0.25, px: 0.5 }}>
                                Patient Value
                              </TableCell>
                            )}
                          <TableCell align="center" sx={{ py: 0.25, px: 0.5 }}>
                            Abnormal
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.25, px: 0.5 }}>
                            Print
                          </TableCell>
                          <TableCell sx={{ py: 0.25, px: 0.5 }}>
                            Range
                          </TableCell>
                          <TableCell sx={{ py: 0.25, px: 0.5 }}>Unit</TableCell>
                          <TableCell sx={{ py: 0.25, px: 0.5 }}>
                            Parameter Type
                          </TableCell>
                          <TableCell sx={{ py: 0.25, px: 0.5 }}>
                            Identifier
                          </TableCell>
                          <TableCell sx={{ py: 0.25, px: 0.5, width: 140 }}>
                            C-Formula
                          </TableCell>

                          <TableCell
                            align="center"
                            sx={{ py: 0.25, px: 0.5, width: 84 }}
                          >
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {currentParameters.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={
                                activeTest?.testResultType ===
                                  "Parameterized" &&
                                  activeTest?.testResultTemplate ===
                                  "Test Result with Patient Values"
                                  ? 11
                                  : 10
                              }
                              align="center"
                              sx={{ py: 1, backgroundColor: "#F3F6FB" }}
                            >
                              No parameters
                            </TableCell>
                          </TableRow>
                        ) : (
                          currentParameters.map((param, idx) => (
                            <TableRow
                              key={idx}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                                backgroundColor: "transparent",
                                "& .MuiTableCell-root": {
                                  borderBottom: "0",
                                  padding: "0px 2px",
                                  height: 32,
                                },
                              }}
                            >
                              <TableCell sx={{ width: 180 }}>
                                <TextField
                                  size="small"
                                  className="custom-input"
                                  fullWidth
                                  value={param.parameter}
                                  InputProps={{ readOnly: !param.isNew }}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      backgroundColor: param.isNew
                                        ? "#fff"
                                        : "transparent", // 👈 now works
                                    },
                                  }}
                                  onChange={e =>
                                    param.isNew &&
                                    updateParameter(
                                      activeIndex,
                                      idx,
                                      "parameter",
                                      e.target.value
                                    )
                                  }
                                />
                              </TableCell>

                              <TableCell sx={{ width: 190 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.25,
                                  }}
                                >
                                  <Autocomplete
                                    size="small"
                                    fullWidth
                                    options={param.resultValues ?? []}
                                    value={param.result ?? ""}
                                    inputValue={param.result ?? ""}
                                    onChange={(event, newValue) => {
                                      const next = String(newValue || "");
                                      const inputType =
                                        param.resultInputType ??
                                        getDefaultResultInputType(
                                          param.resultValues
                                        );
                                      updateParameter(
                                        activeIndex,
                                        idx,
                                        "result",
                                        inputType === "number"
                                          ? sanitizeNumericResultInput(next)
                                          : next
                                      );
                                    }}
                                    onInputChange={(
                                      event,
                                      inputValue,
                                      reason
                                    ) => {
                                      if (
                                        reason !== "input" &&
                                        reason !== "clear"
                                      ) {
                                        return;
                                      }
                                      const inputType =
                                        param.resultInputType ??
                                        getDefaultResultInputType(
                                          param.resultValues
                                        );
                                      updateParameter(
                                        activeIndex,
                                        idx,
                                        "result",
                                        inputType === "number"
                                          ? sanitizeNumericResultInput(
                                              inputValue
                                            )
                                          : inputValue
                                      );
                                    }}
                                    freeSolo
                                    renderInput={params => {
                                      const inputType =
                                        param.resultInputType ??
                                        getDefaultResultInputType(
                                          param.resultValues
                                        );
                                      return (
                                        <TextField
                                          {...params}
                                          ref={el =>
                                            (resultInputRefs.current[idx] = el)
                                          }
                                          onKeyDown={e => {
                                            if (inputType === "number") {
                                              const controlKeys = [
                                                "Backspace",
                                                "Delete",
                                                "Tab",
                                                "Escape",
                                                "Enter",
                                                "ArrowLeft",
                                                "ArrowRight",
                                                "ArrowUp",
                                                "ArrowDown",
                                                "Home",
                                                "End",
                                              ];
                                              if (
                                                e.ctrlKey ||
                                                e.metaKey ||
                                                e.altKey ||
                                                controlKeys.includes(e.key)
                                              ) {
                                                // allow
                                              } else if (
                                                !isNumericResultKeyAllowed(
                                                  e.key,
                                                  String(param.result ?? "")
                                                )
                                              ) {
                                                e.preventDefault();
                                              }
                                            }
                                            handleResultKeyDown(e, idx);
                                          }}
                                          onPaste={e => {
                                            if (inputType !== "number") return;
                                            e.preventDefault();
                                            const pasted =
                                              e.clipboardData.getData("text");
                                            updateParameter(
                                              activeIndex,
                                              idx,
                                              "result",
                                              sanitizeNumericResultInput(pasted)
                                            );
                                          }}
                                          inputProps={{
                                            ...params.inputProps,
                                            inputMode:
                                              inputType === "number"
                                                ? "decimal"
                                                : "text",
                                          }}
                                          className="custom-input"
                                          sx={{
                                            "& .MuiInputBase-root": {
                                              backgroundColor:
                                                param.computationFormula &&
                                                param.computationFormula.trim() !==
                                                  ""
                                                  ? param.result &&
                                                    param.result !== ""
                                                    ? "#fff3cd"
                                                    : "#e3f2fd"
                                                  : "#fff",
                                            },
                                          }}
                                        />
                                      );
                                    }}
                                  />
                                  <Tooltip
                                    title={
                                      (param.resultInputType ??
                                        getDefaultResultInputType(
                                          param.resultValues
                                        )) === "number"
                                        ? "Number mode"
                                        : "Text mode"
                                    }
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexShrink: 0,
                                        gap: "1px",
                                        ml: 0.25,
                                      }}
                                    >
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: "9px",
                                          fontWeight: 700,
                                          lineHeight: 1,
                                          color:
                                            (param.resultInputType ??
                                              getDefaultResultInputType(
                                                param.resultValues
                                              )) === "text"
                                              ? "#1976d2"
                                              : "#9e9e9e",
                                          textTransform: "none",
                                          userSelect: "none",
                                        }}
                                      >
                                        T
                                      </Typography>
                                      <Switch
                                        size="small"
                                        checked={
                                          (param.resultInputType ??
                                            getDefaultResultInputType(
                                              param.resultValues
                                            )) === "number"
                                        }
                                        onChange={e => {
                                          const nextType = e.target.checked
                                            ? "number"
                                            : "text";
                                          setTestResultsState(prev =>
                                            prev.map((tr, i) => {
                                              if (i !== activeIndex) return tr;
                                              const params = (
                                                tr.testParameterToPerform as TestParameter[]
                                              ).map((p, j) => {
                                                if (j !== idx) return p;
                                                return {
                                                  ...p,
                                                  resultInputType: nextType,
                                                  result:
                                                    nextType === "number" &&
                                                    p.result
                                                      ? sanitizeNumericResultInput(
                                                          String(p.result)
                                                        )
                                                      : p.result,
                                                };
                                              });
                                              return {
                                                ...tr,
                                                testParameterToPerform: params,
                                              };
                                            })
                                          );
                                        }}
                                        sx={{
                                          width: 22,
                                          height: 12,
                                          padding: 0,
                                          "& .MuiSwitch-switchBase": {
                                            padding: "1px",
                                            "&.Mui-checked": {
                                              transform: "translateX(10px)",
                                              color: "#fff",
                                              "& + .MuiSwitch-track": {
                                                backgroundColor: "#1976d2",
                                                opacity: 1,
                                              },
                                            },
                                          },
                                          "& .MuiSwitch-thumb": {
                                            width: 10,
                                            height: 10,
                                            boxShadow: "none",
                                          },
                                          "& .MuiSwitch-track": {
                                            borderRadius: 8,
                                            opacity: 1,
                                            backgroundColor: "#bdbdbd",
                                          },
                                        }}
                                        inputProps={{
                                          "aria-label":
                                            "Toggle result input type",
                                        }}
                                      />
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: "9px",
                                          fontWeight: 700,
                                          lineHeight: 1,
                                          color:
                                            (param.resultInputType ??
                                              getDefaultResultInputType(
                                                param.resultValues
                                              )) === "number"
                                              ? "#1976d2"
                                              : "#9e9e9e",
                                          textTransform: "none",
                                          userSelect: "none",
                                        }}
                                      >
                                        N
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </Box>
                              </TableCell>

                              {activeTest?.testResultType === "Parameterized" &&
                                activeTest?.testResultTemplate ===
                                "Test Result with Patient Values" && (
                                  <TableCell sx={{ width: 150 }}>
                                    <TextField
                                      size="small"
                                      className="custom-input"
                                      fullWidth
                                      value={param.patientValue ?? ""}
                                      placeholder="Patient Value"
                                      sx={{
                                        "& .MuiInputBase-root": {
                                          backgroundColor: "#fff",
                                        },
                                      }}
                                      onChange={e =>
                                        updateParameter(
                                          activeIndex,
                                          idx,
                                          "patientValue",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </TableCell>
                                )}

                              <TableCell
                                align="center"
                                sx={{ width: 50, p: 0 }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={!!param.abnormal}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      backgroundColor: "#fff", // always editable
                                    },
                                  }}
                                  onChange={e =>
                                    updateParameter(
                                      activeIndex,
                                      idx,
                                      "abnormal",
                                      e.target.checked
                                    )
                                  }
                                  color="error"
                                />
                              </TableCell>

                              <TableCell
                                align="center"
                                sx={{ width: 50, p: 0 }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={!!param.printOnReport}
                                  onChange={e =>
                                    updateParameter(
                                      activeIndex,
                                      idx,
                                      "printOnReport",
                                      e.target.checked
                                    )
                                  }
                                />
                              </TableCell>

                              <TableCell sx={{ width: 180 }}>
                                <TextField
                                  size="small"
                                  className="custom-input"
                                  // placeholder="range"
                                  fullWidth
                                  value={
                                    !param.femaleRange ||
                                      param.femaleRange.includes("undefined") ||
                                      param.femaleRange.includes("null")
                                      ? ""
                                      : param.femaleRange
                                  }
                                  InputProps={{ readOnly: !param.isNew }}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      backgroundColor: param.isNew
                                        ? "#fff"
                                        : "transparent", // 👈 editable → white
                                    },
                                  }}
                                  onChange={e =>
                                    param.isNew &&
                                    updateParameter(
                                      activeIndex,
                                      idx,
                                      "femaleRange",
                                      e.target.value
                                    )
                                  }
                                />
                              </TableCell>

                              <TableCell sx={{ width: 160 }}>
                                <TextField
                                  size="small"
                                  className="custom-input"
                                  // placeholder="mg/ml"
                                  fullWidth
                                  value={param.unit ?? ""}
                                  InputProps={{ readOnly: !param.isNew }}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      backgroundColor: param.isNew
                                        ? "#fff"
                                        : "transparent", // 👈 editable → white
                                    },
                                  }}
                                  onChange={e =>
                                    param.isNew &&
                                    updateParameter(
                                      activeIndex,
                                      idx,
                                      "unit",
                                      e.target.value
                                    )
                                  }
                                />
                              </TableCell>

                              <TableCell sx={{ width: 160 }}>
                                <TextField
                                  size="small"
                                  className="custom-input"
                                  fullWidth
                                  value={param.patientType?.parameterType ?? ""}
                                  placeholder="Parameter Type"
                                  InputProps={{ readOnly: true }}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      backgroundColor: "#f5f5f5",
                                    },
                                  }}
                                />
                              </TableCell>

                              <TableCell sx={{ width: 160 }}>
                                <TextField
                                  size="small"
                                  className="custom-input"
                                  fullWidth
                                  value={param.parameterIdentifier ?? ""}
                                  placeholder="Identifier"
                                  InputProps={{ readOnly: true }}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      backgroundColor: "#f5f5f5",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ width: 160 }}>
                                <TextField
                                  size="small"
                                  className="custom-input"
                                  fullWidth
                                  value={param.computationFormula ?? ""}
                                  placeholder="C-Formula"
                                  InputProps={{ readOnly: true }}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      backgroundColor: "#f5f5f5",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ width: 84, p: 0 }}
                              >
                                {param.isNew ? (
                                  <Tooltip
                                    title="Delete custom parameter"
                                    arrow
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        removeParameter(activeIndex, idx)
                                      }
                                      aria-label="delete-parameter"
                                      sx={{
                                        color: "error.main",
                                        // fontSize: "0.79rem",
                                      }}
                                    >
                                      <CloseIcon sx={{ fontSize: "0.98rem" }} />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Box
                                    sx={{
                                      color: "text.secondary",
                                      fontSize: 12,
                                    }}
                                  >
                                    —
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Box
                  sx={{
                    mb: 2,
                    mt: 2,
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap", // allows wrapping if screen is too small
                    flexDirection: "row", // side by side
                  }}
                >
                  {!isCultureTest && (
                    <>
                      <TextField
                        label="Result Value"
                        size="small"
                        fullWidth
                        className="custom-input"
                        value={testResultsState[activeIndex]?.resultValue || ""}
                        onChange={e =>
                          setTestResultsState(prev =>
                            prev.map((tr, idx) =>
                              idx === activeIndex
                                ? { ...tr, resultValue: e.target.value }
                                : tr
                            )
                          )
                        }
                        sx={{ flex: 1 }} // take equal width
                      />
                      <TextField
                        label="Viral Load"
                        size="small"
                        fullWidth
                        className="custom-input"
                        value={testResultsState[activeIndex]?.viralLoad || ""}
                        onChange={e =>
                          setTestResultsState(prev =>
                            prev.map((tr, idx) =>
                              idx === activeIndex
                                ? { ...tr, viralLoad: e.target.value }
                                : tr
                            )
                          )
                        }
                        sx={{ flex: 1 }} // take equal width
                      />
                    </>
                  )}
                </Box>
              )}
            </div>
          )}

          <Grid container spacing={1}>
            {/* Notes stays the same */}
            <Grid item xs={12} sm={12}>
              <TextField
                size="small"
                label="Notes"
                name="notes"
                value={activeTest?.notes ?? ""}
                onChange={e =>
                  setTestResultsState(prev =>
                    prev.map((tr, i) =>
                      i === activeIndex ? { ...tr, notes: e.target.value } : tr
                    )
                  )
                }
                className="custom-input"
                fullWidth
                multiline
                rows={2}
                sx={{
                  "& .MuiInputBase-root": { backgroundColor: "#fff" },
                  "& textarea": { resize: "both", maxHeight: "50px" },
                }}
              />
            </Grid>

            {/* Comments */}
            <Grid item xs={12} sm={6}>
              {isPCRTest && !isCultureTest ? (
                <Box
                  sx={{
                    borderBottom: "1px solid #ccc",
                    maxHeight: "200px",
                    overflow: "hidden", // container hides overflow
                    "& .ql-editor": {
                      maxHeight: "200px",
                      overflowY: "auto", // scroll inside editor
                      padding: "8px",
                      boxSizing: "border-box",
                      backgroundColor: "white",
                    },
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={activeTest?.comments || ""}
                    onChange={value =>
                      setTestResultsState(prev =>
                        prev.map((tr, i) =>
                          i === activeIndex ? { ...tr, comments: value } : tr
                        )
                      )
                    }
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ["bold", "italic", "underline"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ align: [] }],
                        [{ color: [] }, { background: [] }],
                        ["link", "image"],
                        ["clean"],
                      ],
                    }}
                    style={{
                      maxHeight: "200px",
                      overflow: "hidden",
                      // background: "#f5f5f5",
                    }}
                    placeholder="Enter PCR Special Test Comments"
                  />
                </Box>
              ) : (
                <TextField
                  size="small"
                  label="Comments"
                  name="comments"
                  className="custom-input"
                  value={activeTest?.comments ?? ""}
                  onChange={e =>
                    setTestResultsState(prev =>
                      prev.map((tr, i) =>
                        i === activeIndex
                          ? { ...tr, comments: e.target.value }
                          : tr
                      )
                    )
                  }
                  fullWidth
                  multiline
                  rows={2}
                  sx={{
                    borderRadius: "10px",
                    "& .MuiInputBase-root": { backgroundColor: "#fff" },
                    "& .MuiOutlinedInput-root": {
                      border: "none",
                      outline: "none",
                    },
                    "& fieldset": { border: "none" },
                    "& textarea": { resize: "both", maxHeight: "50px" },
                  }}
                />
              )}
            </Grid>

            {/* Method */}
            <Grid item xs={6}>
              {isPCRTest && !isCultureTest ? (
                <Box
                  sx={{
                    borderBottom: "1px solid #ccc",
                    maxHeight: "200px",
                    overflow: "hidden", // container hides overflow
                    "& .ql-editor": {
                      maxHeight: "200px",
                      overflowY: "auto", // scroll inside editor
                      padding: "8px",
                      boxSizing: "border-box",
                      backgroundColor: "white",
                    },
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={activeTest?.method || ""}
                    onChange={value =>
                      setTestResultsState(prev =>
                        prev.map((tr, i) =>
                          i === activeIndex ? { ...tr, method: value } : tr
                        )
                      )
                    }
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ["bold", "italic", "underline"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ align: [] }],
                        [{ color: [] }, { background: [] }],
                        ["link", "image"],
                        ["clean"],
                      ],
                    }}
                    style={{
                      maxHeight: "200px",
                      overflow: "hidden",
                    }}
                    placeholder="Enter PCR Special Test Method"
                  />
                </Box>
              ) : (
                <TextField
                  size="small"
                  label="Method"
                  className="custom-input"
                  name="method"
                  value={activeTest?.method ?? ""}
                  onChange={e =>
                    setTestResultsState(prev =>
                      prev.map((tr, i) =>
                        i === activeIndex
                          ? { ...tr, method: e.target.value }
                          : tr
                      )
                    )
                  }
                  fullWidth
                  multiline
                  rows={2}
                  sx={{
                    borderRadius: "10px",
                    "& .MuiInputBase-root": { backgroundColor: "#fff" },
                    "& .MuiOutlinedInput-root": {
                      border: "none",
                      outline: "none",
                    },
                    "& fieldset": { border: "none" },
                    "& textarea": { resize: "vertical", maxHeight: "50px" },
                  }}
                />
              )}
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 0.5,
              mt: 1,
            }}
          >
            <Box>
              {activeApprovalStatus.includes("Approved") && (
                <Typography sx={{ color: "red", fontSize: ".9rem" }}>
                  {`This test is approved by: ${approvedByDoctor}`}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {activeApprovalStatus.includes("Approval") && (
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    !labAccess?.can_approve_result
                      ? "No access to approve result"
                      : "Approve Performed Tests"
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ApprovalOutlined />}
                      onClick={() => setOpenApprovedAllDialog(true)}
                      disabled={!labAccess?.can_approve_result}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.8rem",
                        minWidth: "auto",
                        padding: "2px 4px",
                        "& .MuiButton-startIcon": {
                          marginRight: "4px",
                        },
                      }}
                    >
                      Approve Performed Tests
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Cancel">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    // startIcon={<Cancel />}
                    onClick={() => navigate(-1)}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.8rem",
                      minWidth: "auto",
                      padding: "2px 4px",
                      "& .MuiButton-startIcon": {
                        marginRight: "4px", // default is 8px
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  activeApprovalStatus.includes("Approved")
                    ? "Already Saved"
                    : "Save"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Save sx={{ height: "16px" }} />}
                    onClick={handleSave}
                    disabled={activeApprovalStatus.includes("Approved")}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.8rem",
                      minWidth: "auto",
                      padding: "2px 4px",
                      "& .MuiButton-startIcon": {
                        marginRight: "4px", // default is 8px
                      },
                    }}
                  >
                    {activeIndex < testResultsState.length - 1
                      ? "Save & Next"
                      : "Save & Exit"}
                  </Button>
                </span>
              </Tooltip>
              {activeApprovalStatus.includes("Approval") && (
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    !labAccess?.can_approve_result
                      ? "No access to approve result"
                      : "Approved Test"
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Task />}
                      onClick={handleOpen}
                      disabled={!labAccess?.can_approve_result}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.8rem",
                        minWidth: "auto",
                        padding: "2px 4px",
                        backgroundColor: labAccess?.can_approve_result
                          ? "primary.main"
                          : "#ccc",
                        color: labAccess?.can_approve_result ? "white" : "#666",
                        cursor: labAccess?.can_approve_result
                          ? "pointer"
                          : "not-allowed",
                        "&:hover": {
                          backgroundColor: labAccess?.can_approve_result
                            ? "primary.dark"
                            : "#ccc",
                        },
                        "& .MuiButton-startIcon": {
                          marginRight: "4px", // default is 8px
                        },
                      }}
                    >
                      Approved
                    </Button>
                  </span>
                </Tooltip>
              )}
              {activeApprovalStatus.includes("Approved") && (
                <Tooltip title="Print Preview">
                  <span>
                    <Button
                      onClick={() => {
                        setSelectedSampleId(sampleCollectionId || "");
                        const tId = testResultsState.map(
                          (test: any) => test._id
                        );
                        setTestId(tId);
                        setOpenPrintDialog(true);
                        setAllReports([]);
                      }}
                      variant="contained"
                      size="small"
                      startIcon={<Print />}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.8rem",
                        minWidth: "auto",
                        padding: "2px 4px",
                        "& .MuiButton-startIcon": {
                          marginRight: "4px", // default is 8px
                        },
                      }}
                    >
                      Print Preview
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
            <Dialog open={open} onClose={handleClose}>
              <DialogTitle>
                <WarningAmber /> Confirm Approval
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to approve this test?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="inherit">
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  variant="contained"
                  color="primary"
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog open={openRefreshDialog} onClose={handleClose}>
              <DialogTitle>
                <WarningAmber /> Confirm Reperform
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {`Are you sure you want to reperform ${activeTest?.testName ?? ""
                    } ?`}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setOpenRefreshDialog(false)}
                  color="inherit"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    reperform();
                    setOpenRefreshDialog(false);
                  }}
                  variant="contained"
                  color="primary"
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>

            {/* Approved All Performed Tests Dialog */}
            <Dialog open={openApprovedAllDialog} onClose={() => setOpenApprovedAllDialog(false)}>
              <DialogTitle>
                <WarningAmber /> Confirm Approve All Performed Tests
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to approve all performed tests for this sample collection?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenApprovedAllDialog(false)} color="inherit">
                  Cancel
                </Button>
                <Button
                  onClick={handleApprovedAllConfirm}
                  variant="contained"
                  color="primary"
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>

            {/* Critical Values Dialog */}
            <Dialog
              open={openCriticalDialog}
              onClose={handleCriticalDialogCancel}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "#fff3cd",
                  color: "#856404",
                  padding: 0.5,
                }}
              >
                <WarningAmber sx={{ color: "#ff9800" }} />
                Critical Values Detected
              </DialogTitle>
              <DialogContent sx={{ mt: 2 }}>
                <DialogContentText
                  sx={{ mb: 2, fontSize: "1rem", fontWeight: 500 }}
                >
                  The following parameters have results outside their critical
                  range:
                </DialogContentText>

                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #ddd",
                    backgroundColor: "#f8f9fa",
                    overflowX: "auto", // Ensure table is scrollable on small screens
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      minWidth: 600, // Ensure table doesn't cramp up
                      "& .MuiTableCell-root": {
                        padding: "4px 8px",
                        fontSize: "0.875rem",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#e9ecef" }}>
                        <TableCell sx={{ fontWeight: 600, py: 0 }}>
                          Parameter
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0 }}>
                          Result
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0 }}>
                          Unit
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0 }}>
                          C. Min
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0 }}>
                          C. Max
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0 }}>
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {criticalParameters.map((param, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500, py: 0 }}>
                            {param.parameter}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#dc3545",
                              py: 0,
                            }}
                          >
                            {param.result}
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>{param.unit}</TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            {param.criticalMin !== null
                              ? param.criticalMin
                              : "-"}
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            {param.criticalMax !== null
                              ? param.criticalMax
                              : "-"}
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.25,
                                px: 0.5,
                                py: 0.125,
                                borderRadius: 0.5,
                                // backgroundColor: "#f8d7da",
                                color: "red",
                                fontSize: "0.7rem",
                                fontWeight: 500,
                              }}
                            >
                              {param.criticalType ===
                                "Below Critical Minimum" ? (
                                <KeyboardDoubleArrowDownOutlinedIcon
                                  sx={{ fontSize: "1.5rem" }}
                                />
                              ) : (
                                <KeyboardDoubleArrowUpOutlinedIcon
                                  sx={{ fontSize: "1.5rem" }}
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: "0.85rem" }}>
                    Report Critical Values Information
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        className="custom-input"
                        size="small"
                        label="Informed To"
                        required
                        value={criticalInfo.informedTo}
                        onChange={(e) => setCriticalInfo({ ...criticalInfo, informedTo: e.target.value })}
                        inputProps={{ style: { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          label="Date"
                          className="custom-input"
                          value={dayjs(criticalInfo.date)}
                          onChange={(newValue) => setCriticalInfo({ ...criticalInfo, date: newValue || dayjs() })}
                          slotProps={{ 
                            textField: { 
                              size: 'small', 
                              fullWidth: true, 
                              required: true,
                              inputProps: { style: { fontSize: '0.875rem' } }
                            } 
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Autocomplete
                        className="custom-input"
                        options={Object.values(InfoMode)}
                        value={criticalInfo.infoMode}
                        onChange={(_, newValue) => setCriticalInfo({ ...criticalInfo, infoMode: newValue || InfoMode.PHONE_CALL })}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Info Mode" 
                            size="small" 
                            required 
                            inputProps={{ ...params.inputProps, style: { fontSize: '0.875rem' } }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        className="custom-input"
                        size="small"
                        label="Informed By"
                        required
                        value={criticalInfo.informedBy}
                        onChange={(e) => setCriticalInfo({ ...criticalInfo, informedBy: e.target.value })}
                        inputProps={{ style: { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        className="custom-input"
                        fullWidth
                        size="small"
                        label="Comment"
                        multiline
                        rows={1}
                        value={criticalInfo.comment}
                        onChange={(e) => setCriticalInfo({ ...criticalInfo, comment: e.target.value })}
                        inputProps={{ style: { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button
                  onClick={handleCriticalDialogCancel}
                  color="inherit"
                  variant="outlined"
                  sx={{ padding: 0 }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCriticalDialogProceed}
                  variant="contained"
                  color="warning"
                  sx={{
                    padding: "0.1rem 0.5rem",
                    backgroundColor: "#ff9800",
                    "&:hover": {
                      backgroundColor: "#f57c00",
                    },
                  }}
                >
                  Proceed
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </>
      )}
      <HistoryPrintDialog
        open={openHistoryDialog}
        allReports={previouseTests}
        onClose={() => setOpenHistoryDialog(false)}
        selectedSampleId={sampleCollectionId || 0}
      />
      <ReportPrintDialog
        open={openPrintDialog}
        selectedSampleId={selectedSampleId}
        onClose={() => setOpenPrintDialog(false)}
        testId={testId}
        allReports={allReports}
        patientData={patientData}
      />
    </Box>
  );
}
